import {Expressions} from '@packages/systems/wfdl';
import type {Expression} from '@packages/systems/wfdl-ast';
import {
  createPopoverTargetExpression,
  getPopoverTargetReference,
  type PopoverTargetReference,
} from './popover/expression';
import {
  createModalTargetExpression,
  getModalTargetReference,
  type ModalTargetReference,
} from './modal/expression';

export type OverlayTargetReference =
  | ({kind: 'popover'} & PopoverTargetReference)
  | ({kind: 'modal'} & ModalTargetReference);

/** Reads a valid Popover or Modal target reference, tagged with its kind. */
export const getOverlayTargetReference = (
  expression: Expression
): OverlayTargetReference | null => {
  const popoverReference = getPopoverTargetReference(expression);
  if (popoverReference) return {kind: 'popover', ...popoverReference};

  const modalReference = getModalTargetReference(expression);
  return modalReference ? {kind: 'modal', ...modalReference} : null;
};

/** Whether an expression holds a Popover or Modal target reference anywhere. */
export const containsOverlayTarget = Expressions.some(
  (expression) => getOverlayTargetReference(expression) != null
);

export const createOverlayTargetExpression = (
  reference: OverlayTargetReference
): Expression => {
  const {componentKey, targetId} = reference;
  switch (reference.kind) {
    case 'modal':
      return createModalTargetExpression({
        componentKey,
        targetId,
        action: reference.action,
      });
    case 'popover':
      return createPopoverTargetExpression({
        componentKey,
        targetId,
        action: reference.action,
      });
    default:
      return reference satisfies never;
  }
};

/**
 * Rewrites every Popover and Modal target reference in an expression through
 * an old id -> new id map, like `remapPopoverTargetReferences`. Each reference
 * keeps its kind and action; ids missing from the map are preserved.
 */
export const remapOverlayTargetReferences = (
  expression: Expression,
  idMap: {[oldId: string]: string}
): Expression =>
  Expressions.everywhere((expr) => {
    const reference = getOverlayTargetReference(expr);
    if (!reference) return expr;

    const componentKey = idMap[reference.componentKey];
    const targetId = idMap[reference.targetId];
    if (componentKey == null && targetId == null) return expr;

    return createOverlayTargetExpression({
      ...reference,
      componentKey: componentKey ?? reference.componentKey,
      targetId: targetId ?? reference.targetId,
    });
  })(expression);

/**
 * Rewrites valid Popover and Modal references anywhere in an expression, like
 * `rewritePopoverTargetReferences`. Callers that process each element
 * independently can stop at descendant element data.
 */
export const rewriteOverlayTargetReferences = (
  expression: Expression,
  rewrite: (
    reference: OverlayTargetReference,
    expression: Expression
  ) => Expression,
  {traverseElements = true}: {traverseElements?: boolean} = {}
): Expression => {
  const rewriteReference = (currentExpression: Expression) => {
    const reference = getOverlayTargetReference(currentExpression);
    return reference
      ? rewrite(reference, currentExpression)
      : currentExpression;
  };

  if (traverseElements) {
    return Expressions.everywhere(rewriteReference)(expression);
  }

  const referencePaths = Expressions.findPathsWithStoppable(
    expression,
    (currentExpression) => {
      if (Expressions.isElement(currentExpression)) {
        return Expressions.stop(false);
      }
      if (getOverlayTargetReference(currentExpression)) {
        return Expressions.stop(true);
      }
      return Expressions.resume(false);
    }
  );

  return referencePaths.reduce(
    (currentExpression, path) =>
      Expressions.updateIn(currentExpression, path, rewriteReference),
    expression
  );
};

/** Rewrites ownership of valid Popover and Modal target references. */
export const rewriteOverlayTargetOwner = (
  expression: Expression,
  oldOwner: string,
  newOwner: string
): Expression =>
  Expressions.everywhere((expr) => {
    const reference = getOverlayTargetReference(expr);
    if (!reference || reference.componentKey !== oldOwner) {
      return expr;
    }

    return createOverlayTargetExpression({
      ...reference,
      componentKey: newOwner,
    });
  })(expression);
